import Vue from "vue";
import $axios from "../../api";

const PREFIX = "unionweb/member/";

// --- Queued export polling -------------------------------------------------
// Backend flow: trigger endpoint returns 202 + export_log_id → poll status
// until 'success'/'failed' → download the generated xlsx via dedicated
// endpoint. Default poll interval 5s and 20 min cap matches the observed
// worst-case (100k rows ≈ 15 min).
const EXPORT_POLL_INTERVAL_MS = 5000;
const EXPORT_POLL_MAX_ATTEMPTS = 240;

// Guard urutan request datatable (last-request-wins) — lihat getDashboardDatatable.
let datatableSeq = 0;

async function runQueuedExport({ endpoint, params = {}, fallbackFileName, onProgress }) {
  const triggerResp = await $axios.get(`member-union/export/${endpoint}`, {
    params,
    validateStatus: (s) => s === 202,
  });

  const exportLogId = triggerResp.data && triggerResp.data.export_log_id;
  if (!exportLogId) {
    throw new Error("Export was not queued (missing export_log_id).");
  }

  for (let attempt = 0; attempt < EXPORT_POLL_MAX_ATTEMPTS; attempt++) {
    await new Promise((r) => setTimeout(r, EXPORT_POLL_INTERVAL_MS));

    const { data: status } = await $axios.get(
      `member-union/export-status/${exportLogId}`
    );

    if (onProgress && status.progress_total) {
      const pct = Math.min(
        99,
        Math.round((status.progress_done / status.progress_total) * 100)
      );
      onProgress(pct);
    }

    if (status.status === "success") {      const dlResp = await $axios.get(
        `member-union/export-download/${exportLogId}`,
        { responseType: "blob" }
      );

      const contentType =
        dlResp.headers["content-type"] ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([dlResp.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);

      let fileName = status.file_name || fallbackFileName;
      const disposition = dlResp.headers["content-disposition"];
      if (disposition) {
        const match = disposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        if (match) {
          try {
            fileName = decodeURIComponent(match[1].replace(/['"]/g, ""));
          } catch (e) {
            fileName = match[1].replace(/['"]/g, "");
          }
        }
      }

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      return;
    }

    if (status.status === "failed") {
      throw new Error(status.error_message || "Export failed on the worker.");
    }
  }

  throw new Error(
    `Export is still running after ${(EXPORT_POLL_INTERVAL_MS * EXPORT_POLL_MAX_ATTEMPTS) / 60000} minutes. Check the Export Logs page for current status.`
  );
}

const state = () => ({
  page: 1,
  isLoading: false,
  isSuccess: false,
  searchLoading: false,
  statisticLoading: false,
  datatableLoading: false,
  searchMemberLoading: false,
  segmentLoading: false,
  exportAllMemberLoading: false,
  exportVipOnlineLoading: false,
  exportVipOfflineLoading: false,
  exportVipUnionLoading: false,
  exportRegulerMemberLoading: false,
  exportActiveMemberLoading: false,
  exportActiveMemberMonthLoading: false,
  exportVerifiedLoading: false,
  exportDatatableLoading: false,
  isErrors: "",
  list: [],
  dashboardData: {
    statistic: {},
    datatable: {},
  },
  searchMemberList: [],
  segmentList: {},
  memberDetail: {
    summary: {},
    transaction: {},
  },
  spgList: [],
  spgListLoading: false,
  exportInsentifLoading: false,
  memberCardList: [],
  memberCardListLoading: false,
  orderNumberList: [],
  orderNumberListLoading: false,
  upgradeMemberLoading: false,
  memberPointHistory: [],
  memberPointHistoryLoading: false,
  memberGrowthData: null,
  exporting: false,
  lastExported: null,
  exportProgressPercent: 0,
  exportProgressStartedAt: null,
  exportProgressLabel: "",
  memberOverview: null,
  summaryLoading: false,
  sendOfferLoading: false,
  transactionDetail: {
    loading: false,
    error: null,
    data: null,
  },
});

const mutations = {
  SET_PAGE(state, payload) {
    state.page = payload;
  },
  SET_LOADING(state, payload) {
    state.isLoading = payload;
  },
  SET_SEARCH_LOADING(state, payload) {
    state.searchLoading = payload;
  },
  SET_STATISTIC_LOADING(state, payload) {
    state.statisticLoading = payload;
  },
  SET_DATATABLE_LOADING(state, payload) {
    state.datatableLoading = payload;
  },
  SET_SEARCH_MEMBER_LOADING(state, payload) {
    state.searchMemberLoading = payload;
  },
  SET_SEGMENT_LOADING(state, payload) {
    state.segmentLoading = payload;
  },
  SET_EXPORT_ALL_MEMBER_LOADING(state, payload) {
    state.exportAllMemberLoading = payload;
  },
  SET_EXPORT_VERIFIED_LOADING(state, payload) {
    state.exportVerifiedLoading = payload;
  },
  SET_EXPORT_DATATABLE_LOADING(state, payload) {
    state.exportDatatableLoading = payload;
  },
  SET_EXPORT_VIP_ONLINE_LOADING(state, payload) {
    state.exportVipOnlineLoading = payload;
  },
  SET_EXPORT_VIP_OFFLINE_LOADING(state, payload) {
    state.exportVipOfflineLoading = payload;
  },
  SET_EXPORT_VIP_UNION_LOADING(state, payload) {
    state.exportVipUnionLoading = payload;
  },
  SET_EXPORT_REGULER_MEMBER_LOADING(state, payload) {
    state.exportRegulerMemberLoading = payload;
  },
  SET_EXPORT_ACTIVE_MEMBER_LOADING(state, payload) {
    state.exportActiveMemberLoading = payload;
  },
  SET_EXPORT_ACTIVE_MEMBER_MONTH_LOADING(state, payload) {
    state.exportActiveMemberMonthLoading = payload;
  },
  ASSIGN_ERRORS(state, payload) {
    state.isErrors = payload;
  },
  SET_IS_SUCCESS(state, payload) {
    state.isSuccess = payload;
  },
  SET_LIST(state, payload) {
    state.list = payload;
  },
  ASSIGN_MEMBER_SUMMARY_DATA(state, payload) {
    // state.memberDetail.summary = payload
    for (const key in payload) {
      Vue.set(state.memberDetail.summary, key, payload[key])
    }
  },
  ASSIGN_MEMBER_TRANSACTION_DATA(state, payload) {
    if (!state.memberDetail.transaction) {
      state.memberDetail.transaction = {};
    }

    state.memberDetail.transaction = {
      ...state.memberDetail.transaction,
      ...payload
    };
  },
  ASSIGN_MEMBER_STATISTIC_DATA(state, payload) {
    state.dashboardData.statistic = payload
  },
  ASSIGN_MEMBER_DATATABLE_DATA(state, payload) {
    state.dashboardData.datatable = payload
  },
  ASSIGN_SEARCH_MEMBER_DATA(state, payload) {
    state.searchMemberList = payload
  },
  ASSIGN_SEGMENT_DATA(state, payload) {
    state.segmentList = payload
  },
  ASSIGN_SPG_LIST_DATA(state, payload) {
    state.spgList = payload
  },
  SET_SPG_LIST_LOADING(state, payload) {
    state.spgListLoading = payload
  },
  SET_EXPORT_INSENTIF_LOADING(state, payload) {
    state.exportInsentifLoading = payload;
  },
  SET_MEMBER_CARD_LIST(state, payload) {
    state.memberCardList = payload
  },
  SET_MEMBER_CARD_LIST_LOADING(state, payload) {
    state.memberCardListLoading = payload
  },
  SET_ORDER_NUMBER_LIST(state, payload) {
    state.orderNumberList = payload
  },
  SET_ORDER_NUMBER_LIST_LOADING(state, payload) {
    state.orderNumberListLoading = payload
  },
  SET_UPGRADE_MEMBER_LOADING(state, payload) {
    state.upgradeMemberLoading = payload
  },
  ASSIGN_MEMBER_POINT_HISTORY(state, payload) {
    state.memberPointHistory = payload
  },
  SET_MEMBER_POINT_HISTORY_LOADING(state, payload) {
    state.memberPointHistoryLoading = payload
  },
  RESET_MEMBER_POINT_HISTORY(state) {
    state.memberPointHistory = []
  },
  ASSIGN_MEMBER_GROWTH_DATA(state, payload) {
    state.memberGrowthData = payload
  },
  ASSIGN_MEMBER_OVERVIEW(state, payload) {
    state.memberOverview = payload
  },
  SET_EXPORTING(state, payload) {
    state.exporting = payload
  },
  SET_LAST_EXPORTED(state, payload) {
    state.lastExported = payload
  },
  SET_EXPORT_PROGRESS_PERCENT(state, payload) {
    state.exportProgressPercent = payload
  },
  // Single transaction that opens a new export's progress lifecycle:
  // sets label, anchors startedAt for elapsed-time display, zeroes percent.
  SET_EXPORT_BEGIN(state, { label }) {
    state.exportProgressLabel     = label || "Export";
    state.exportProgressStartedAt = Date.now();
    state.exportProgressPercent   = 0;
  },
  SET_EXPORT_END(state) {
    state.exportProgressLabel     = "";
    state.exportProgressStartedAt = null;
    state.exportProgressPercent   = 0;
  },
  SET_SUMMARY_LOADING(state, payload) {
    state.summaryLoading = payload
  },
  SET_SEND_OFFER_LOADING(state, payload) {
    state.sendOfferLoading = payload
  },
  SET_TRANSACTION_DETAIL_LOADING(state, payload) {
    state.transactionDetail.loading = payload
  },
  SET_TRANSACTION_DETAIL_ERROR(state, payload) {
    state.transactionDetail.error = payload
  },
  ASSIGN_TRANSACTION_DETAIL_DATA(state, payload) {
    state.transactionDetail.data = payload
  },
  RESET_TRANSACTION_DETAIL(state) {
    state.transactionDetail = {
      loading: false,
      error: null,
      data: null,
    }
  },
};

const getters = {
  getOtherStatistic: (state) => {
    return Object.fromEntries(
      Object.entries(state.dashboardData?.statistic?.statistics ?? {}).filter(([key]) => key !== 'summary')
    ) ?? {}
  },
  // True whenever any member export is in-flight — drives the global
  // miniplayer bubble so it persists across route changes.
  isAnyExporting: (state) =>
    state.exportAllMemberLoading
    || state.exportVerifiedLoading
    || state.exportDatatableLoading
    || state.exportVipOnlineLoading
    || state.exportVipOfflineLoading
    || state.exportVipUnionLoading
    || state.exportRegulerMemberLoading
    || state.exportActiveMemberLoading
    || state.exportActiveMemberMonthLoading
    || state.exporting,
  getFormattedOrderNumberList: (state) => {
    return state.orderNumberList.map(item => ({
      label: `${item.order_number} - ${Number(item.grandtotal).toLocaleString('id-ID', { style: 'currency', currency: 'IDR' })}`,
      value: item.order_number
    }));
  }
}

const actions = {
  getList({ commit, state, rootState, dispatch }, payload) {
    commit("SET_LOADING", true);
    return new Promise((resolve, reject) => {
      $axios
        .get(`${PREFIX}list?page=${state.page}&view=${rootState.Main.view}${payload || ""}`)
        .then((res) => {
          commit("SET_LIST", res.data.data);
          commit("SET_LOADING", false);
        })
        .catch((error) => {
          commit("SET_LOADING", false);
          dispatch("Main/globalSweetAlert", error.response, { root: true });
        });
    });
  },
  async getDashboardStatistic({ commit, state, rootState, dispatch }, payload) {
    commit("SET_STATISTIC_LOADING", true);
    try {
      const { data } = await $axios.get(`member-union/dashboard/statistics`)

      commit("ASSIGN_MEMBER_STATISTIC_DATA", data.data)
    } catch (error) {
      dispatch("Main/globalSweetAlert", error.response, { root: true });
    } finally {
      commit("SET_STATISTIC_LOADING", false);
    }
  },
  async getDashboardDatatable({ commit, state, rootState, dispatch }, payload) {
    // Guard urutan request: filter text 2s debounce vs select tanpa debounce
    // bisa balapan; tanpa guard, response lambat filter LAMA menimpa yang baru.
    const seq = ++datatableSeq;
    commit("SET_DATATABLE_LOADING", true);
    try {
      const { data } = await $axios.get(`member-union/dashboard/list`, {
        params: {
          page: state.page,
          view: rootState.Main.view,
          ...payload
        }
      })

      if (seq === datatableSeq) commit("ASSIGN_MEMBER_DATATABLE_DATA", data.data)
    } catch (error) {
      // Error dari request basi ikut di-drop — toast telat cuma membingungkan.
      if (seq === datatableSeq) dispatch("Main/globalSweetAlert", error.response, { root: true });
    } finally {
      if (seq === datatableSeq) commit("SET_DATATABLE_LOADING", false);
    }
  },
  async getSearchMember({ commit, state, rootState, dispatch }, payload) {
    commit("SET_SEARCH_MEMBER_LOADING", true);

    try {
      const { data } = await $axios.get(`member-union/filter/member`, {
        params: {
          search: payload?.search
        }
      })

      commit("ASSIGN_SEARCH_MEMBER_DATA", data.data)
    } catch (error) {
      dispatch("Main/globalSweetAlert", error.response, { root: true });
    } finally {
      commit("SET_SEARCH_MEMBER_LOADING", false,);
    }
  },
  async getSegmentData({ commit, state, rootState, dispatch }, payload) {
    commit("SET_SEGMENT_LOADING", true);

    try {
      const { data } = await $axios.get(`member-union/filter/tag`)

      commit("ASSIGN_SEGMENT_DATA", data.data)
    } catch (error) {
      dispatch("Main/globalSweetAlert", error.response, { root: true });
    } finally {
      commit("SET_SEGMENT_LOADING", false,);
    }
  },
  async getMemberSummaryData({ commit, state, rootState, dispatch }, payload) {
    commit("SET_SUMMARY_LOADING", true);

    try {
      const { data } = await $axios.get(`member-union/summary`, {
        params: {
          // page: state.page,
          // view: rootState.Main.view,
          uuid: payload?.uuid,
          // section: payload?.section,
          // start_date: payload?.start_date,
          // end_date: payload?.end_date,
          // sortdesc: payload?.sortdesc,
          // sortby: payload?.sortby,
        }
      })

      commit("SET_IS_SUCCESS", true)
      commit("ASSIGN_MEMBER_SUMMARY_DATA", data.data)
    } catch (error) {
      dispatch("Main/globalSweetAlert", error.response, { root: true });
    } finally {
      commit("SET_SUMMARY_LOADING", false);
    }
  },
  async getMemberTransactionData({ commit, state, rootState, dispatch }, payload) {
    commit(payload.searchLoading ? "SET_SEARCH_LOADING" : "SET_LOADING", true);

    try {
      const { data } = await $axios.get(`member-union/transaction`, {
        params: {
          page: state.page,
          view: rootState.Main.view,
          uuid: payload?.uuid,

          // PENTING: Tambahkan parameter tab dan filter pencarian
          tab: payload?.tab,
          start_date: payload?.start_date,
          end_date: payload?.end_date,
          sortdesc: payload?.sortdesc,
          sortby: payload?.sortby,
          order_number: payload?.order_number,
          product_name: payload?.product_name,
          payment_name: payload?.payment_name,
          platform: payload?.platform,
        }
      });

      // PENTING: Cek dulu apakah data 'transaction' ada di dalam payload
      // Jika user sedang membuka tab 'cart', blok kode ini akan aman dilewati
      if (data.data && data.data.transaction && data.data.transaction.data) {
        data.data.transaction.data.forEach(element => {
          element.show_more_product = false;
        });
      }

      commit("SET_IS_SUCCESS", true);
      commit("ASSIGN_MEMBER_TRANSACTION_DATA", data.data);

    } catch (error) {
      dispatch("Main/globalSweetAlert", error.response, { root: true });
    } finally {
      // Matikan loading state
      commit(payload.searchLoading ? "SET_SEARCH_LOADING" : "SET_LOADING", false);
    }
  },
  async exportAllMembers({ commit, dispatch }) {
    commit("SET_EXPORT_ALL_MEMBER_LOADING", true);
    commit("SET_EXPORT_BEGIN", { label: "Export Semua Member" });
    try {
      const date = new Date().toISOString().slice(0, 10);
      await runQueuedExport({
        endpoint: "all",
        fallbackFileName: `Export_Total_Member_${date}.xlsx`,
        onProgress: (pct) => commit("SET_EXPORT_PROGRESS_PERCENT", pct),
      });
    } catch (error) {
      dispatch("Main/globalSweetAlert", error.response || { data: { message: error.message } }, { root: true });
    } finally {
      commit("SET_EXPORT_ALL_MEMBER_LOADING", false);
      commit("SET_EXPORT_END");
    }
  },
  async exportVerifiedMember({ commit, dispatch }) {
    commit("SET_EXPORT_VERIFIED_LOADING", true);
    commit("SET_EXPORT_BEGIN", { label: "Export Member Terverifikasi" });
    try {
      const date = new Date().toISOString().slice(0, 10);
      await runQueuedExport({
        endpoint: "member-verified",
        fallbackFileName: `Export_Verified_Member_${date}.xlsx`,
        onProgress: (pct) => commit("SET_EXPORT_PROGRESS_PERCENT", pct),
      });
    } catch (error) {
      dispatch("Main/globalSweetAlert", error.response || { data: { message: error.message } }, { root: true });
    } finally {
      commit("SET_EXPORT_VERIFIED_LOADING", false);
      commit("SET_EXPORT_END");
    }
  },
  async exportDatatableMember({ commit, dispatch }, payload) {
    commit("SET_EXPORT_DATATABLE_LOADING", true);
    commit("SET_EXPORT_BEGIN", { label: "Export Data Terfilter" });
    try {
      const date = new Date().toISOString().slice(0, 10);
      await runQueuedExport({
        endpoint: "datatable",
        params: { ...(payload || {}) },
        fallbackFileName: `Export_Summary_Member_${date}.xlsx`,
        onProgress: (pct) => commit("SET_EXPORT_PROGRESS_PERCENT", pct),
      });
    } catch (error) {
      dispatch("Main/globalSweetAlert", error.response || { data: { message: error.message } }, { root: true });
    } finally {
      commit("SET_EXPORT_DATATABLE_LOADING", false);
      commit("SET_EXPORT_END");
    }
  },
  async exportVipOnline({ commit, dispatch }) {
    commit("SET_EXPORT_VIP_ONLINE_LOADING", true);
    commit("SET_EXPORT_BEGIN", { label: "Export VIP Online" });
    try {
      const date = new Date().toISOString().slice(0, 10);
      await runQueuedExport({
        endpoint: "vip-online",
        fallbackFileName: `Export_Vip_Online_${date}.xlsx`,
        onProgress: (pct) => commit("SET_EXPORT_PROGRESS_PERCENT", pct),
      });
    } catch (error) {
      dispatch("Main/globalSweetAlert", error.response || { data: { message: error.message } }, { root: true });
    } finally {
      commit("SET_EXPORT_VIP_ONLINE_LOADING", false);
      commit("SET_EXPORT_END");
    }
  },
  async exportVipOffline({ commit, dispatch }) {
    commit("SET_EXPORT_VIP_OFFLINE_LOADING", true);
    commit("SET_EXPORT_BEGIN", { label: "Export VIP Offline" });
    try {
      const date = new Date().toISOString().slice(0, 10);
      await runQueuedExport({
        endpoint: "vip-offline",
        fallbackFileName: `Export_Vip_Offline_${date}.xlsx`,
        onProgress: (pct) => commit("SET_EXPORT_PROGRESS_PERCENT", pct),
      });
    } catch (error) {
      dispatch("Main/globalSweetAlert", error.response || { data: { message: error.message } }, { root: true });
    } finally {
      commit("SET_EXPORT_VIP_OFFLINE_LOADING", false);
      commit("SET_EXPORT_END");
    }
  },
  async exportVipUnion({ commit, dispatch }) {
    commit("SET_EXPORT_VIP_UNION_LOADING", true);
    commit("SET_EXPORT_BEGIN", { label: "Export VIP Online + Offline" });
    try {
      const date = new Date().toISOString().slice(0, 10);
      await runQueuedExport({
        endpoint: "vip-union",
        fallbackFileName: `Export_Vip_Union_${date}.xlsx`,
        onProgress: (pct) => commit("SET_EXPORT_PROGRESS_PERCENT", pct),
      });
    } catch (error) {
      dispatch("Main/globalSweetAlert", error.response || { data: { message: error.message } }, { root: true });
    } finally {
      commit("SET_EXPORT_VIP_UNION_LOADING", false);
      commit("SET_EXPORT_END");
    }
  },
  async exportRegulerMember({ commit, dispatch }) {
    commit("SET_EXPORT_REGULER_MEMBER_LOADING", true);
    commit("SET_EXPORT_BEGIN", { label: "Export Regular Member" });
    try {
      const date = new Date().toISOString().slice(0, 10);
      await runQueuedExport({
        endpoint: "reguler-member",
        fallbackFileName: `Export_Reguler_Member_${date}.xlsx`,
        onProgress: (pct) => commit("SET_EXPORT_PROGRESS_PERCENT", pct),
      });
    } catch (error) {
      dispatch("Main/globalSweetAlert", error.response || { data: { message: error.message } }, { root: true });
    } finally {
      commit("SET_EXPORT_REGULER_MEMBER_LOADING", false);
      commit("SET_EXPORT_END");
    }
  },
  async exportActiveMember({ commit, dispatch }, payload) {
    const loadingMutation = payload?.this_month
      ? "SET_EXPORT_ACTIVE_MEMBER_MONTH_LOADING"
      : "SET_EXPORT_ACTIVE_MEMBER_LOADING";
    const label = payload?.this_month ? "Export Member Aktif Bulan Ini" : "Export Member Aktif";
    commit(loadingMutation, true);
    commit("SET_EXPORT_BEGIN", { label });
    try {
      const date = new Date().toISOString().slice(0, 10);
      await runQueuedExport({
        endpoint: "member-aktif",
        fallbackFileName: `Export_Active_Member_${date}.xlsx`,
        onProgress: (pct) => commit("SET_EXPORT_PROGRESS_PERCENT", pct),
      });
    } catch (error) {
      dispatch("Main/globalSweetAlert", error.response || { data: { message: error.message } }, { root: true });
    } finally {
      commit(loadingMutation, false);
      commit("SET_EXPORT_END");
    }
  },
  async getSpgList({ commit, state, rootState, dispatch }, payload) {
    commit("SET_SPG_LIST_LOADING", true);

    try {
      const { data } = await $axios.get(`member-union/spg/list`, {
        params: {
          find: payload?.search
        }
      });

      commit("ASSIGN_SPG_LIST_DATA", data.data);
    }
    catch (error) {
      dispatch("Main/globalSweetAlert", error.response, { root: true });
    } finally {
      commit("SET_SPG_LIST_LOADING", false);
    }
  },
  async exportInsentif({ commit, dispatch }, payload) {
    commit("SET_EXPORT_INSENTIF_LOADING", true);
    try {
      const { month, year } = payload;

      const response = await $axios.post(
        `unionweb/member/export/insentif`,
        { month, year },
        { responseType: "blob" }
      );

      const contentType = response.headers["content-type"];

      if (
        contentType &&
        contentType.includes(
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        )
      ) {
        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const contentDisposition = response.headers["content-disposition"];
        let fileName = `Insentif_VIP_${month}_${year}.xlsx`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (match) {
            try {
              fileName = decodeURIComponent(match[1].replace(/['"]/g, ""));
            } catch {
              fileName = match[1].replace(/['"]/g, "");
            }
          }
        }

        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        Swal.fire({
          icon: "success",
          title: "Berhasil",
          text: `File "${fileName}" berhasil diunduh.`,
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });

        return;
      }

      const text = await response.data.text();
      let json = {};
      try {
        json = JSON.parse(text);
      } catch {
        json = { message: "Terjadi kesalahan saat memproses file." };
      }

      dispatch(
        "Main/globalSweetAlert",
        {
          status: 400,
          data: { message: json.message },
        },
        { root: true }
      );
    } catch (error) {
      dispatch("Main/globalSweetAlert", error.response, { root: true });
    } finally {
      commit("SET_EXPORT_INSENTIF_LOADING", false);
    }
  },
  getMemberCardcodeList({ commit, state, rootState, dispatch }, payload) {
    commit("SET_MEMBER_CARD_LIST_LOADING", true);
    return new Promise((resolve, reject) => {
      $axios
        .get(`member-union/cardcode-list`, {
          params: {
            card_code: payload?.card_code,
          }
        })
        .then((res) => {
          commit("SET_MEMBER_CARD_LIST", res.data.data);
          resolve()
        })
        .catch((error) => {
          dispatch("Main/globalSweetAlert", error.response, { root: true });
          reject(error)
        })
        .finally(() => {
          commit("SET_MEMBER_CARD_LIST_LOADING", false);
        });
    });
  },
  getOrderNumberList({ commit, state, rootState, dispatch }, payload) {
    commit("SET_ORDER_NUMBER_LIST_LOADING", true);
    return new Promise((resolve, reject) => {
      $axios
        .get(`member-union/ordernumber-list`, {
          params: {
            member_type: payload?.member_type,
            order_number: payload?.order_number,
            store_code: payload?.store_code,
            web_id: payload?.web_id,
          }
        })
        .then((res) => {
          commit("SET_ORDER_NUMBER_LIST", res.data.data);
          resolve()
        })
        .catch((error) => {
          dispatch("Main/globalSweetAlert", error.response, { root: true });
          reject(error)
        }).finally(() => {
          commit("SET_ORDER_NUMBER_LIST_LOADING", false);
        });
    });
  },
  upgradeVipMember({ commit, state, rootState, dispatch }, payload) {
    commit("SET_UPGRADE_MEMBER_LOADING", true);
    return new Promise((resolve, reject) => {
      $axios
        .put(`member-union/upgrade-vip`, payload)
        .then(() => {
          resolve();
        })
        .catch((error) => {
          dispatch("Main/globalSweetAlert", error.response, { root: true });
          reject();
        })
        .finally(() => {
          commit("SET_UPGRADE_MEMBER_LOADING", false);
        });
    });
  },
  fetchMemberPointHistory({ commit, dispatch }, payload) {
    commit("SET_MEMBER_POINT_HISTORY_LOADING", true);
    return new Promise((resolve, reject) => {
      $axios.get(`unionweb/store/minidash/member/point/log`, {
        params: {
          card_code: payload,
        }
      })
        .then((res) => {
          commit("ASSIGN_MEMBER_POINT_HISTORY", res.data.data);
          resolve();
        })
        .catch((error) => {
          dispatch("Main/globalSweetAlert", error.response, { root: true });
          reject();
        })
        .finally(() => {
          commit("SET_MEMBER_POINT_HISTORY_LOADING", false);
        });
    });
  },
  fetchMemberGrowthData({ commit, dispatch }, payload) {
    commit("SET_LOADING", true);
    return new Promise((resolve, reject) => {
      $axios.get(`member-union/member-growth`, {
        params: {
          start_date: payload?.start_date,
          end_date: payload?.end_date,
        }
      })
        .then((res) => {
          commit("ASSIGN_MEMBER_GROWTH_DATA", res.data.data);
          resolve();
        })
        .catch((error) => {
          dispatch("Main/globalSweetAlert", error.response, { root: true });
          reject();
        })
        .finally(() => {
          commit("SET_LOADING", false);
        });
    });
  },
  // Generic export action used by the dashboard member-statistic tiles
  // (sectionMemberDetail.vue) — covers tier tiles ('green'|'silver'|'gold'|
  // 'platinum') and the active-member tile ('member-aktif').
  // Same queued + polled flow as the other export actions so the global
  // progress bubble renders uniformly.
  async exportMember({ commit, dispatch }, endpoint) {
    const labels = {
      green:          "Export Member Green",
      silver:         "Export Member Silver",
      gold:           "Export Member Gold",
      platinum:       "Export Member Platinum",
      "member-aktif": "Export Member Aktif Bulan Ini",
    };

    commit("SET_EXPORTING", true);
    commit("SET_EXPORT_BEGIN", { label: labels[endpoint] || `Export ${endpoint}` });

    try {
      const date = new Date().toISOString().slice(0, 10);
      await runQueuedExport({
        endpoint,
        fallbackFileName: `Export_${endpoint}_${date}.xlsx`,
        onProgress: (pct) => commit("SET_EXPORT_PROGRESS_PERCENT", pct),
      });
      commit("SET_LAST_EXPORTED", endpoint);
    } catch (error) {
      console.error("Export member gagal:", error);
      dispatch(
        "Main/globalSweetAlert",
        error.response || { data: { message: error.message } },
        { root: true }
      );
      throw error;
    } finally {
      commit("SET_EXPORTING", false);
      commit("SET_EXPORT_END");
    }
  },
  getMemberOverview({ commit, dispatch }, payload) {
    commit("SET_LOADING", true);
    return new Promise((resolve, reject) => {
      $axios.get(`unionweb/member/overview`, {
        params: {
          uuid: payload?.uuid,
        }
      })
        .then((res) => {
          commit("ASSIGN_MEMBER_OVERVIEW", res.data.data);
          resolve();
        })
        .catch((error) => {
          dispatch("Main/globalSweetAlert", error.response, { root: true });
          reject();
        })
        .finally(() => {
          commit("SET_LOADING", false);
        });
    });
  },
  sendMemberOffer({ commit, dispatch }, payload) {
    commit("SET_SEND_OFFER_LOADING", true);
    return new Promise((resolve, reject) => {
      $axios.post(`member-union/send-offer`, payload)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          dispatch("Main/globalSweetAlert", error.response, { root: true });
          reject(error);
        })
        .finally(() => {
          commit("SET_SEND_OFFER_LOADING", false);
        });
    });
  },
  async getTransactionDetail({ commit, dispatch }, payload) {
    commit("SET_TRANSACTION_DETAIL_LOADING", true);
    commit("SET_TRANSACTION_DETAIL_ERROR", null);
    
    try {
      const { data } = await $axios.get('member-union/transaction/detail', {
        params: {
          uuid: payload?.uuid,
          order_number: payload?.order_number
        }
      });
      commit("ASSIGN_TRANSACTION_DETAIL_DATA", data.data);
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Gagal memuat detail transaksi.';
      commit("SET_TRANSACTION_DETAIL_ERROR", errorMessage);
      dispatch("Main/globalSweetAlert", error.response, { root: true });
    } finally {
      commit("SET_TRANSACTION_DETAIL_LOADING", false);
    }
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
